/** Locale-aware formatting helpers. Default locale is French (the base locale). */

export function formatDistance(meters: number, locale = 'fr-FR'): string {
  if (meters < 1000) return `${Math.round(meters)} m`
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(meters / 1000)} km`
}

export function formatDuration(seconds: number): string {
  const m = Math.round(seconds / 60)
  if (m < 60) return `${m} min`
  const h = Math.floor(m / 60)
  const rem = m % 60
  return rem === 0 ? `${h} h` : `${h} h ${rem}`
}

export function formatDateTime(iso: string, locale = 'fr-FR'): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? '—'
    : new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(d)
}

/**
 * A timestamp as the activity feeds write it (AF frames `4898:3522` and
 * `5449:7479`, AC `4594:5544`).
 *
 * <p>"Il y a 2 heures" while it is still today, "Hier" for yesterday, and
 * "17:33 · 15 May" once it is old enough that a relative phrase stops telling
 * anyone anything. The frames use all three, and which one applies is a
 * function of how long ago it was rather than a style choice per screen.
 */
export function formatActivityTime(iso: string, locale = 'fr-FR'): string {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return '—'

  const minutes = Math.floor((Date.now() - at.getTime()) / 60000)
  if (minutes < 1) return 'À l’instant'
  if (minutes < 60) return `Il y a ${minutes} minute${minutes > 1 ? 's' : ''}`

  const hours = Math.floor(minutes / 60)
  const startOfToday = new Date()
  startOfToday.setHours(0, 0, 0, 0)
  if (at >= startOfToday) return `Il y a ${hours} heure${hours > 1 ? 's' : ''}`

  const startOfYesterday = new Date(startOfToday)
  startOfYesterday.setDate(startOfYesterday.getDate() - 1)
  if (at >= startOfYesterday) return 'Hier'

  const time = new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(at)
  const day = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(at)
  // The interpunct the frame draws between them, not a comma.
  return `${time} · ${day}`
}

/** "+22961234567" -> "+229 61 23 45 67" */
export function formatPhone(e164: string): string {
  const m = /^\+229(\d{2})(\d{2})(\d{2})(\d{2})$/.exec(e164)
  return m ? `+229 ${m[1]} ${m[2]} ${m[3]} ${m[4]}` : e164
}

/**
 * Mask a phone number for any surface where the counterparty can see it.
 * Spec §10.1.6 requires direct contact details to be hidden between users.
 */
export function maskPhone(e164: string): string {
  const tail = e164.slice(-2)
  return `+229 •• •• •• ${tail}`
}
